import { Image, Typography } from "antd";
import { useTranslation } from "react-i18next";
import type { BlogMediaItem } from "../../types/blogPost";
import { getBlogMediaPublicUrl, parseVideoEmbed } from "../../utils/blogMediaUtils";

const { Title } = Typography;

interface BlogPostMediaGalleryProps {
  media: BlogMediaItem[];
}

function GalleryVideo({ url, title }: { url: string; title: string }) {
  const embed = parseVideoEmbed(url);
  if (!embed) return null;
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingBottom: "56.25%",
        height: 0,
        overflow: "hidden",
        borderRadius: 4,
        background: "#000",
      }}
    >
      <iframe
        src={embed}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
        }}
      />
    </div>
  );
}

export function BlogPostMediaGallery({ media }: BlogPostMediaGalleryProps) {
  const { t } = useTranslation("translation");

  if (!media.length) return null;

  return (
    <div style={{ marginTop: 32 }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        {t("blog.gallery")}
      </Title>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <Image.PreviewGroup>
          {media.map((item) => {
            if (item.type === "image") {
              const url = getBlogMediaPublicUrl(item.path);
              if (!url) return null;
              return (
                <Image
                  key={item.id}
                  src={url}
                  alt={item.alt ?? ""}
                  style={{
                    width: "100%",
                    maxHeight: 480,
                    objectFit: "contain",
                    borderRadius: 4,
                    background: "#fafafa",
                  }}
                />
              );
            }
            return (
              <GalleryVideo
                key={item.id}
                url={item.url}
                title={t("blog.videoEmbed")}
              />
            );
          })}
        </Image.PreviewGroup>
      </div>
    </div>
  );
}
